(function () {
    'use strict';

    const root = document.querySelector('[data-home-nearby]');
    if (!root) return;

    const elements = {
        map: root.querySelector('[data-home-nearby-map]'),
        input: root.querySelector('[data-home-nearby-location]'),
        clear: root.querySelector('[data-home-nearby-clear]'),
        locate: root.querySelector('[data-home-nearby-locate]'),
        cards: root.querySelector('[data-home-nearby-cards]'),
        loadMore: root.querySelector('[data-home-nearby-load-more]'),
        count: root.querySelector('[data-home-nearby-count]'),
        status: root.querySelector('[data-home-nearby-status]'),
        loading: root.querySelector('[data-home-nearby-loading]'),
        empty: root.querySelector('[data-home-nearby-empty]'),
        error: root.querySelector('[data-home-nearby-error]'),
        permission: root.querySelector('[data-home-nearby-permission]'),
        retry: root.querySelector('[data-home-nearby-retry]'),
    };

    const initialCoordinates = {
        lat: Number(root.dataset.initialLat) || 51.5072682,
        lng: Number(root.dataset.initialLng) || -0.1657303,
    };
    const initialZoom = Number(root.dataset.initialZoom) || 14;
    const initialLabel = root.dataset.initialLabel || 'Hyde Park';
    const radius = Number(root.dataset.radius) || 8;
    let map;
    let autocomplete;
    let visitorMarker;
    let escortMarkers = [];
    let infoWindow;
    let currentCoordinates = initialCoordinates;
    let currentLabel = initialLabel;
    let requestController;
    let initialized = false;
    let dragTimer;
    let currentBounds = null;
    let requestGeneration = 0;
    let locationAttempt = 0;
    let nearbyLocations = [];
    let nextPage = null;
    let canFavourite = false;
    const profileCards = new Map();
    // Pins keyed by position so re-renders (e.g. the remaining result pages
    // arriving after load) keep unchanged pins, and an open preview with them.
    const markersByKey = new Map();
    let activeIds = [];

    // Server-rendered initial state (cards already in the DOM + pin data as JSON):
    // hydrate from it instead of requesting on load. Searches, "use my location"
    // and map panning still request fresh results and re-render list and pins.
    let hydratedInitial = false;
    let skipNextIdleFetch = false;
    // Opening a preview auto-pans the map; that movement must not count as the
    // visitor exploring (which refetches and rebuilds the pins, closing the preview).
    let suppressMapMoveUntil = 0;
    // Position key of the pin whose preview is open, so a pin rebuilt with new
    // members (remaining pages arriving) gets its preview re-opened, not dropped.
    let openPreviewKey = null;

    function openInfoWindow(content, marker) {
        suppressMapMoveUntil = Date.now() + 1500;
        openPreviewKey = marker.positionKey || null;
        infoWindow.setContent(content);
        infoWindow.open({ map: map, anchor: marker });
    }

    function closeInfoWindow() {
        openPreviewKey = null;
        if (infoWindow) infoWindow.close();
    }

    function hydrateInitialState() {
        const dataElement = root.querySelector('[data-home-nearby-initial]');
        if (!dataElement) return false;
        let data;
        try {
            data = JSON.parse(dataElement.textContent || '{}');
        } catch (error) {
            return false;
        }
        const locations = Array.isArray(data.locations) ? data.locations : [];
        if (!locations.length) return false;

        nearbyLocations = locations;
        nextPage = data.has_more ? Number(data.next_page) : null;
        canFavourite = Boolean(data.can_favourite);
        profileCards.clear();
        elements.cards.querySelectorAll('.home-nearby-card').forEach(function (card) {
            profileCards.set(String(card.dataset.staffId), card);
        });
        // Every location needs its card (renderMarkers links pins to cards).
        if (locations.some(function (item) { return !profileCards.has(String(item.id)); })) {
            renderCards(nearbyLocations, canFavourite);
        }
        hydratedInitial = true;
        return true;
    }

    function safeUrl(value, requireSameOrigin) {
        try {
            const parsed = new URL(String(value || ''), window.location.origin);
            if (!['http:', 'https:'].includes(parsed.protocol)) return '';
            if (requireSameOrigin && parsed.origin !== window.location.origin) return '';
            return parsed.href;
        } catch (error) {
            return '';
        }
    }

    function textElement(tagName, className, value) {
        const element = document.createElement(tagName);
        if (className) element.className = className;
        element.textContent = value == null ? '' : String(value);
        return element;
    }

    function formatPrice(value) {
        if (!value) return '';
        return new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP',
            maximumFractionDigits: 0,
        }).format(Number(value));
    }

    function milesFromKm(value) {
        return (Number(value) * 0.621371).toFixed(Number(value) < 16 ? 1 : 0);
    }

    function loadScript(src, id) {
        return new Promise(function (resolve, reject) {
            if (id && document.getElementById(id)) {
                resolve();
                return;
            }

            const script = document.createElement('script');
            script.src = src;
            script.async = true;
            script.defer = true;
            if (id) script.id = id;
            script.onload = resolve;
            script.onerror = function () {
                reject(new Error('Unable to load map assets.'));
            };
            document.head.appendChild(script);
        });
    }

    function loadGoogleMaps() {
        if (window.google && window.google.maps) return Promise.resolve();

        return new Promise(function (resolve, reject) {
            const callbackName = '__homeNearbyGoogleMapsReady';
            window[callbackName] = function () {
                delete window[callbackName];
                resolve();
            };

            const script = document.createElement('script');
            const separator = root.dataset.googleMapsUrl.indexOf('?') === -1 ? '?' : '&';
            script.src = root.dataset.googleMapsUrl + separator + 'callback=' + callbackName;
            script.id = 'home-nearby-google-maps';
            script.async = true;
            script.defer = true;
            script.onerror = function () {
                delete window[callbackName];
                reject(new Error('Google Maps could not be loaded.'));
            };
            document.head.appendChild(script);
        });
    }

    function loadMapAssets() {
        return loadGoogleMaps();
    }

    function setState(name) {
        ['loading', 'empty', 'error', 'permission'].forEach(function (stateName) {
            if (elements[stateName]) elements[stateName].hidden = stateName !== name;
        });
        elements.cards.hidden = Boolean(name);
        elements.cards.setAttribute('aria-busy', name === 'loading' ? 'true' : 'false');
    }

    function setStatus(message) {
        elements.status.textContent = message;
    }

    function createAdvancedMarker(options) {
        if (google.maps.marker && google.maps.marker.AdvancedMarkerElement) {
            return new google.maps.marker.AdvancedMarkerElement(options);
        }

        return new google.maps.Marker({
            map: options.map,
            position: options.position,
            title: options.title || '',
        });
    }

    function detachMarker(marker) {
        marker.map = null;
        if (typeof marker.setMap === 'function') marker.setMap(null);
    }

    function removeMarkers() {
        closeInfoWindow();
        markersByKey.forEach(detachMarker);
        markersByKey.clear();
        escortMarkers = [];
    }

    function markerIcon(isActive) {
        const colour = isActive ? '#111111' : '#ff385c';
        const svg = '<svg xmlns="http://www.w3.org/2000/svg" width="34" height="42" viewBox="0 0 34 42"><path fill="' + colour + '" stroke="#fff" stroke-width="2" d="M17 1C8.2 1 1 8.2 1 17c0 12 16 24 16 24s16-12 16-24C33 8.2 25.8 1 17 1Z"/><circle cx="17" cy="17" r="5" fill="#fff"/></svg>';
        return {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
            scaledSize: new google.maps.Size(34, 42),
            anchor: new google.maps.Point(17, 42),
        };
    }

    const PIN_SVG = '<svg viewBox="0 0 34 42" aria-hidden="true"><path d="M17 1C8.2 1 1 8.2 1 17c0 12 16 24 16 24s16-12 16-24C33 8.2 25.8 1 17 1Z"/><circle cx="17" cy="17" r="5"/></svg>';

    function createMarkerContent(item) {
        const marker = document.createElement('button');
        marker.type = 'button';
        marker.className = 'home-nearby-map-pin';
        marker.setAttribute('aria-label', 'Show ' + item.name + ' in results');
        marker.setAttribute('aria-pressed', 'false');
        marker.innerHTML = PIN_SVG;
        return marker;
    }

    // Public coordinates are rounded to a ~1km grid, so several escorts often share
    // exactly the same point. They are shown as one pin with a count instead of
    // being spread around it.
    function createClusterContent(items) {
        const marker = document.createElement('button');
        marker.type = 'button';
        marker.className = 'home-nearby-map-pin home-nearby-map-pin--cluster';
        marker.setAttribute('aria-label', 'Show ' + items.length + ' escorts at this location in results');
        marker.setAttribute('aria-pressed', 'false');
        marker.innerHTML = PIN_SVG;
        marker.appendChild(textElement('span', 'home-nearby-map-pin__count', items.length));
        return marker;
    }

    function groupLocations(locations) {
        const groups = new Map();
        locations.forEach(function (item) {
            const key = Number(item.lat).toFixed(6) + ',' + Number(item.lng).toFixed(6);
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push(item);
        });
        return Array.from(groups.values());
    }

    function renderInfoWindow(item, marker) {
        const availability = item.is_available ? 'Available now' : 'Currently away';
        const content = document.createElement('article');
        content.className = 'home-nearby-info';
        const closeButton = document.createElement('button');
        closeButton.type = 'button';
        closeButton.className = 'home-nearby-info__close';
        closeButton.setAttribute('aria-label', 'Close profile preview');
        closeButton.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>';
        closeButton.addEventListener('click', closeInfoWindow);
        const image = document.createElement('img');
        image.src = safeUrl(item.image, false);
        image.alt = '';
        const details = document.createElement('div');
        details.appendChild(textElement('strong', '', item.name));
        details.appendChild(textElement('span', '', item.area || 'London'));
        details.appendChild(textElement('span', '', milesFromKm(item.distance_km) + ' miles away · ' + availability));
        const profileUrl = safeUrl(item.profile_url, true);
        if (profileUrl) {
            const profileButton = textElement('button', 'home-nearby-info__profile', 'View escort');
            profileButton.type = 'button';
            profileButton.addEventListener('click', function () {
                window.location.assign(profileUrl);
            });
            details.appendChild(profileButton);
        }
        content.append(image, details, closeButton);

        openInfoWindow(content, marker);
    }

    function renderGroupInfoWindow(items, marker) {
        const content = document.createElement('article');
        content.className = 'home-nearby-group';
        const head = document.createElement('header');
        head.className = 'home-nearby-group__head';
        head.appendChild(textElement('strong', 'home-nearby-group__title', items.length + ' escorts at this location'));
        const closeButton = document.createElement('button');
        closeButton.type = 'button';
        closeButton.className = 'home-nearby-group__close';
        closeButton.setAttribute('aria-label', 'Close preview');
        closeButton.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>';
        closeButton.addEventListener('click', closeInfoWindow);
        head.appendChild(closeButton);

        const list = document.createElement('ul');
        list.className = 'home-nearby-group__list';
        items.forEach(function (item) {
            const row = document.createElement('li');
            const profileUrl = safeUrl(item.profile_url, true);
            const link = document.createElement(profileUrl ? 'a' : 'div');
            link.className = 'home-nearby-group__item';
            if (profileUrl) link.href = profileUrl;
            const image = document.createElement('img');
            image.src = safeUrl(item.image, false);
            image.alt = '';
            image.loading = 'lazy';
            const details = document.createElement('span');
            details.className = 'home-nearby-group__body';
            details.appendChild(textElement('strong', '', item.name));
            details.appendChild(textElement('span', '', (item.area || 'London') + ' · ' + milesFromKm(item.distance_km) + ' mi · ' + (item.is_available ? 'Available now' : 'Currently away')));
            link.append(image, details);
            row.appendChild(link);
            list.appendChild(row);
        });
        content.append(head, list);

        openInfoWindow(content, marker);
    }

    function scrollCardIntoList(card) {
        const listRect = elements.cards.getBoundingClientRect();
        const cardRect = card.getBoundingClientRect();
        const top = elements.cards.scrollTop + cardRect.top - listRect.top - 8;
        elements.cards.scrollTo({
            top: Math.max(0, top),
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        });
    }

    function focusCard(staffId, revealList) {
        focusCards([staffId], revealList);
    }

    function focusCards(staffIds, revealList) {
        const ids = staffIds.map(String);
        const id = ids[0];
        activeIds = ids;
        profileCards.forEach(function (profileCard, profileId) {
            const isActive = ids.indexOf(profileId) !== -1;
            profileCard.classList.toggle('is-active', isActive);
            profileCard.setAttribute('aria-current', isActive ? 'true' : 'false');
        });
        escortMarkers.forEach(function (marker) {
            const markerIds = (marker.staffIds || [marker.staffId]).map(String);
            const isActive = markerIds.some(function (markerId) { return ids.indexOf(markerId) !== -1; });
            if (marker.pinElement) {
                marker.pinElement.classList.toggle('is-active', isActive);
                marker.pinElement.setAttribute('aria-pressed', isActive ? 'true' : 'false');
            } else if (typeof marker.setIcon === 'function') {
                marker.setIcon(markerIcon(isActive));
            }
            if (typeof marker.setAttribute === 'function') {
                marker.setAttribute('aria-pressed', isActive ? 'true' : 'false');
            }
            if (typeof marker.setZIndex === 'function') marker.setZIndex(isActive ? 10 : 1);
            else marker.zIndex = isActive ? 10 : 1;
        });

        const card = profileCards.get(id);
        if (!card) return;
        scrollCardIntoList(card);
        if (revealList && window.matchMedia('(max-width: 767.98px)').matches) {
            root.querySelector('.home-nearby__results').scrollIntoView({
                behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
                block: 'start',
            });
        }
    }

    function renderMarkers(locations) {
        const seen = new Set();
        groupLocations(locations).forEach(function (items) {
            const key = Number(items[0].lat).toFixed(6) + ',' + Number(items[0].lng).toFixed(6);
            const memberKey = items.map(function (item) { return String(item.id); }).join(',');
            seen.add(key);
            const existing = markersByKey.get(key);
            if (existing && existing.memberKey === memberKey) return; // unchanged: keep it
            const reopenPreview = Boolean(existing) && openPreviewKey === key;
            if (existing) detachMarker(existing);
            const marker = buildMarker(items);
            marker.memberKey = memberKey;
            marker.positionKey = key;
            markersByKey.set(key, marker);
            if (reopenPreview) {
                if (items.length > 1) {
                    renderGroupInfoWindow(items, marker);
                    focusCards(marker.staffIds, false);
                } else {
                    renderInfoWindow(items[0], marker);
                    focusCard(items[0].id, false);
                }
            }
        });
        markersByKey.forEach(function (marker, key) {
            if (seen.has(key)) return;
            if (openPreviewKey === key) openPreviewKey = null;
            detachMarker(marker);
            markersByKey.delete(key);
        });
        escortMarkers = Array.from(markersByKey.values());
        escortMarkers.forEach(function (marker) {
            const markerIds = (marker.staffIds || [marker.staffId]).map(String);
            const isActive = markerIds.some(function (markerId) { return activeIds.indexOf(markerId) !== -1; });
            if (marker.pinElement) {
                marker.pinElement.classList.toggle('is-active', isActive);
                marker.pinElement.setAttribute('aria-pressed', isActive ? 'true' : 'false');
            }
        });
    }

    function buildMarker(items) {
        {
            const isCluster = items.length > 1;
            const first = items[0];
            const pinElement = isCluster ? createClusterContent(items) : createMarkerContent(first);
            const label = isCluster
                ? 'Show ' + items.length + ' escorts at this location in results'
                : 'Show ' + first.name + ' in results';
            const marker = createAdvancedMarker({
                map: map,
                position: { lat: Number(first.lat), lng: Number(first.lng) },
                title: isCluster ? items.length + ' escorts' : first.name,
                zIndex: 1,
                content: pinElement,
                gmpClickable: true,
            });
            marker.staffId = isCluster ? null : first.id;
            marker.staffIds = items.map(function (item) { return item.id; });
            marker.pinElement = pinElement;
            const profileCard = profileCards.get(String(first.id));
            if (profileCard) pinElement.setAttribute('aria-controls', profileCard.id);
            if (typeof marker.setAttribute === 'function') {
                marker.setAttribute('aria-label', label);
                if (profileCard) marker.setAttribute('aria-controls', profileCard.id);
                marker.setAttribute('aria-pressed', 'false');
            }
            const selectMarker = function () {
                if (marker.lastSelectedAt && Date.now() - marker.lastSelectedAt < 250) return;
                marker.lastSelectedAt = Date.now();
                if (isCluster) {
                    renderGroupInfoWindow(items, marker);
                    focusCards(marker.staffIds, false);
                } else {
                    renderInfoWindow(first, marker);
                    focusCard(first.id, false);
                }
            };
            pinElement.addEventListener('click', selectMarker);
            if (google.maps.marker && marker instanceof google.maps.marker.AdvancedMarkerElement) {
                marker.addEventListener('gmp-click', selectMarker);
            } else {
                marker.setIcon(markerIcon(false));
                if (isCluster && typeof marker.setLabel === 'function') {
                    marker.setLabel({ text: String(items.length), color: '#fff', fontWeight: '700' });
                }
                marker.addListener('click', selectMarker);
            }
            return marker;
        }
    }

    function createFavouriteButton(item) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'home-nearby-card__favourite favourites staff_' + item.id;
        button.dataset.type = 'staff';
        button.dataset.id = item.id;
        button.setAttribute('aria-label', 'Toggle ' + item.name + ' as a favourite');

        const icon = document.createElement('i');
        icon.className = 'cu-heart favourites-icon' + (item.is_favourite ? ' active' : '');
        icon.setAttribute('aria-hidden', 'true');
        const message = textElement(
            'span',
            'favourites-message',
            item.is_favourite ? 'Added to favourites!' : 'Removed from favourites!'
        );
        button.append(icon, message);
        return button;
    }

    function renderCards(locations, canFavourite) {
        const fragment = document.createDocumentFragment();
        profileCards.clear();
        locations.forEach(function (item, index) {
            const availabilityClass = item.is_available ? 'is-available' : 'is-away';
            const availability = item.is_available ? 'Available' : (item.back_on || 'Away');
            const rate = item.price
                ? formatPrice(item.price) + (item.rate_name ? ' / ' + item.rate_name : '')
                : 'View rates';
            const profileUrl = safeUrl(item.profile_url, true);

            const card = document.createElement('article');
            card.className = 'home-nearby-card';
            card.dataset.staffId = item.id;
            card.id = 'home-nearby-profile-' + index;
            const isActive = activeIds.indexOf(String(item.id)) !== -1;
            card.classList.toggle('is-active', isActive);
            card.setAttribute('aria-current', isActive ? 'true' : 'false');
            if (profileUrl) {
                card.dataset.profileUrl = profileUrl;
                card.setAttribute('role', 'link');
                card.tabIndex = 0;
                card.setAttribute('aria-label', 'View ' + item.name + ' profile');
            }
            profileCards.set(String(item.id), card);

            const imageHolder = document.createElement('div');
            imageHolder.className = 'home-nearby-card__image';
            const image = document.createElement('img');
            image.src = safeUrl(item.image, false);
            image.loading = 'lazy';
            image.decoding = 'async';
            image.alt = item.name;
            imageHolder.appendChild(image);

            const body = document.createElement('div');
            body.className = 'home-nearby-card__body';
            const titleRow = document.createElement('div');
            titleRow.className = 'home-nearby-card__title-row';
            titleRow.appendChild(textElement('strong', '', item.name));
            body.appendChild(titleRow);
            body.appendChild(textElement(
                'span',
                'home-nearby-card__availability ' + availabilityClass,
                availability
            ));
            body.appendChild(textElement(
                'span',
                'home-nearby-card__meta',
                (item.area || 'London') + ' · ' + milesFromKm(item.distance_km) + ' mi'
            ));
            body.appendChild(textElement('span', 'home-nearby-card__rate', rate));

            card.append(imageHolder, body);
            if (canFavourite) card.appendChild(createFavouriteButton(item));
            fragment.appendChild(card);
        });
        elements.cards.replaceChildren(fragment);
        elements.cards.classList.toggle('is-scrollable', locations.length > 0);
        elements.cards.tabIndex = 0;
        elements.cards.setAttribute('aria-label', 'Scrollable nearby escorts');
    }

    function fetchNearby(coordinates, label, append, generation) {
        append = Boolean(append);
        if (append && (!nextPage || generation !== requestGeneration)) return Promise.resolve();
        if (!append) generation = ++requestGeneration;
        const queryCoordinates = {
            lat: Number(coordinates.lat),
            lng: Number(coordinates.lng),
        };
        const queryLabel = label || currentLabel;
        currentCoordinates = coordinates;
        currentLabel = queryLabel;
        if (requestController) requestController.abort();
        requestController = new AbortController();

        if (!append) {
            const visibleBounds = map && map.getBounds ? map.getBounds() : null;
            if (visibleBounds) {
                const northEast = visibleBounds.getNorthEast();
                const southWest = visibleBounds.getSouthWest();
                currentBounds = {
                    ne_lat: northEast.lat(),
                    ne_lng: northEast.lng(),
                    sw_lat: southWest.lat(),
                    sw_lng: southWest.lng(),
                };
            }
            setState('loading');
            setStatus('Finding escorts near ' + currentLabel + '…');
            elements.count.textContent = 'Finding nearby escorts';
        } else {
            setStatus('Loading more escorts near ' + currentLabel + '…');
        }
        elements.loadMore.disabled = true;

        const csrf = document.querySelector('meta[name="csrf-token"]');
        return fetch(root.dataset.endpoint, {
            method: 'POST',
            signal: requestController.signal,
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': csrf ? csrf.content : '',
                'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({
                lat: queryCoordinates.lat,
                lng: queryCoordinates.lng,
                radius: radius,
                page: append ? nextPage : 1,
                ...(currentBounds || {}),
            }),
        })
            .then(function (response) {
                if (!response.ok) throw new Error('Nearby escorts could not be loaded.');
                return response.json();
            })
            .then(function (data) {
                if (generation !== requestGeneration) return;
                const receivedLocations = Array.isArray(data.locations) ? data.locations : [];
                if (!receivedLocations.length && !append) {
                    nearbyLocations = [];
                    nextPage = null;
                    renderMarkers(nearbyLocations);
                    profileCards.clear();
                    elements.cards.replaceChildren();
                    elements.loadMore.hidden = true;
                    elements.loadMore.disabled = false;
                    setState('empty');
                    elements.count.textContent = 'No nearby escorts';
                    setStatus('No escorts were found within 5 miles of ' + currentLabel + '.');
                    return;
                }

                nearbyLocations = append
                    ? Array.from(new Map(
                        nearbyLocations.concat(receivedLocations).map(function (location) {
                            return [String(location.id), location];
                        })
                    ).values())
                    : receivedLocations;
                nextPage = data.has_more ? Number(data.next_page) : null;
                canFavourite = Boolean(data.can_favourite);

                renderCards(nearbyLocations, canFavourite);
                renderMarkers(nearbyLocations);
                setState(null);
                const total = Number(data.count) || nearbyLocations.length;
                elements.count.textContent = total > nearbyLocations.length
                    ? 'Showing ' + nearbyLocations.length + ' of ' + total + ' nearby escorts'
                    : total + (total === 1 ? ' nearby escort' : ' nearby escorts');
                elements.loadMore.hidden = true;
                elements.loadMore.disabled = false;
                setStatus('Showing ' + nearbyLocations.length + ' of ' + total + ' nearby escorts.');

                if (nextPage) {
                    return fetchNearby(queryCoordinates, queryLabel, true, generation);
                }
            })
            .catch(function (error) {
                if (error.name === 'AbortError' || generation !== requestGeneration) return;
                elements.loadMore.disabled = false;
                if (append) {
                    setStatus('More nearby escorts could not be loaded. Please try again.');
                    return;
                }
                nearbyLocations = [];
                nextPage = null;
                elements.loadMore.hidden = true;
                setState('error');
                elements.count.textContent = 'Please try again';
                setStatus(error.message);
            });
    }

    function setVisitorLocation(coordinates) {
        if (visitorMarker) {
            visitorMarker.map = null;
            if (typeof visitorMarker.setMap === 'function') visitorMarker.setMap(null);
        }

        const pin = document.createElement('div');
        pin.className = 'home-nearby-user-pin';
        pin.innerHTML = '<span></span>';
        visitorMarker = createAdvancedMarker({
            map: map,
            position: coordinates,
            title: 'Your selected location',
            content: pin,
            zIndex: 1000,
        });
    }

    function selectLocation(coordinates, label) {
        currentCoordinates = coordinates;
        currentLabel = label;
        setVisitorLocation(coordinates);
        // Always the same zoom as the initial view, whatever the size of the
        // searched area (fitting the place's own bounds zoomed towns right out).
        map.setCenter(coordinates);
        map.setZoom(initialZoom);
    }

    function configureAutocomplete() {
        autocomplete = new google.maps.places.Autocomplete(elements.input, {
            componentRestrictions: { country: 'gb' },
            fields: ['formatted_address', 'geometry', 'name'],
            bounds: new google.maps.LatLngBounds(
                { lat: 51.2, lng: -0.65 },
                { lat: 51.8, lng: 0.35 }
            ),
            strictBounds: false,
        });

        autocomplete.addListener('place_changed', function () {
            const place = autocomplete.getPlace();
            if (!place.geometry || !place.geometry.location) {
                setStatus('Choose a location from the suggestions.');
                return;
            }
            const coordinates = {
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng(),
            };
            const label = place.name || place.formatted_address || 'your chosen area';
            elements.input.value = place.formatted_address || label;
            elements.clear.hidden = false;
            selectLocation(coordinates, label);
        });
    }

    function useBrowserLocation() {
        if (!navigator.geolocation) {
            setState('permission');
            setStatus('This browser does not support location access. Search by area instead.');
            return;
        }

        const attempt = ++locationAttempt;
        let settled = false;
        const finish = function (callback) {
            if (settled || attempt !== locationAttempt) return;
            settled = true;
            window.clearTimeout(permissionTimer);
            elements.locate.disabled = false;
            elements.locate.classList.remove('is-locating');
            callback();
        };
        const permissionTimer = window.setTimeout(function () {
            finish(function () {
                setState('permission');
                setStatus('Location access was not received. Search by area or check your browser permissions.');
            });
        }, 8000);

        elements.locate.disabled = true;
        elements.locate.classList.add('is-locating');
        setStatus('Waiting for your browser to share your location…');
        navigator.geolocation.getCurrentPosition(
            function (position) {
                finish(function () {
                    elements.input.value = 'My current location';
                    elements.clear.hidden = false;
                    selectLocation({
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    }, 'your location');
                });
            },
            function (error) {
                finish(function () {
                    setState('permission');
                    const message = error.code === error.PERMISSION_DENIED
                        ? 'Location access was declined. Search by area instead.'
                        : 'Your location is unavailable. Search by area instead.';
                    setStatus(message);
                });
            },
            { enableHighAccuracy: true, timeout: 7000, maximumAge: 300000 }
        );
    }

    function initialize() {
        if (initialized) return;
        initialized = true;
        if (!hydratedInitial) hydrateInitialState();
        if (hydratedInitial) {
            setStatus('Loading the map…');
        } else {
            setState('loading');
            setStatus('Loading the nearby map…');
        }

        loadMapAssets()
            .then(function () {
                map = new google.maps.Map(elements.map, {
                    center: initialCoordinates,
                    zoom: initialZoom,
                    mapId: root.dataset.mapId || undefined,
                    mapTypeControl: false,
                    streetViewControl: false,
                    fullscreenControl: true,
                    zoomControl: true,
                    zoomControlOptions: {
                        position: google.maps.ControlPosition.RIGHT_CENTER,
                    },
                    clickableIcons: false,
                    gestureHandling: 'cooperative',
                });
                root.classList.add('is-map-ready');
                infoWindow = new google.maps.InfoWindow({
                    headerDisabled: true,
                });
                configureAutocomplete();

                map.addListener('bounds_changed', function () {
                    if (Date.now() < suppressMapMoveUntil) return;
                    requestGeneration++;
                    clearTimeout(dragTimer);
                    if (requestController) requestController.abort();
                });
                map.addListener('dragstart', function () {
                    currentLabel = 'this map area';
                });
                map.addListener('idle', function () {
                    clearTimeout(dragTimer);
                    if (skipNextIdleFetch) {
                        // First idle after hydrating: the map is settled (bounds_changed has
                        // fired), so now append the remaining pages of the initial results.
                        skipNextIdleFetch = false;
                        if (hydratedInitial && nextPage) {
                            fetchNearby(initialCoordinates, initialLabel, true, requestGeneration);
                        }
                        return;
                    }
                    if (Date.now() < suppressMapMoveUntil) return;
                    dragTimer = setTimeout(function () {
                        const center = map.getCenter();
                        fetchNearby({ lat: center.lat(), lng: center.lng() }, currentLabel);
                    }, 300);
                });

                if (hydratedInitial) {
                    // Pins from the server-rendered data; no request for the first view. The
                    // remaining pages are appended from the first idle event (see above):
                    // starting them here got cancelled by the map's initial bounds_changed.
                    skipNextIdleFetch = true;
                    renderMarkers(nearbyLocations);
                    setState(null);
                    setStatus('Showing ' + nearbyLocations.length + ' nearby escorts.');
                }
            })
            .catch(function (error) {
                initialized = false;
                if (!window.google || !window.google.maps) {
                    document.getElementById('home-nearby-google-maps')?.remove();
                }
                setState('error');
                elements.count.textContent = 'Please try again';
                setStatus(error.message);
            });
    }

    elements.input.addEventListener('input', function () {
        elements.clear.hidden = !elements.input.value;
    });
    elements.clear.addEventListener('click', function () {
        elements.input.value = '';
        elements.clear.hidden = true;
        elements.input.focus();
    });
    elements.locate.addEventListener('click', function () {
        initialize();
        if (map) {
            useBrowserLocation();
        } else {
            const waitForMap = window.setInterval(function () {
                if (!map) return;
                window.clearInterval(waitForMap);
                useBrowserLocation();
            }, 100);
            window.setTimeout(function () { window.clearInterval(waitForMap); }, 12000);
        }
    });
    elements.retry.addEventListener('click', function () {
        if (map) fetchNearby(currentCoordinates, currentLabel);
        else initialize();
    });
    elements.loadMore.addEventListener('click', function () {
        fetchNearby(currentCoordinates, currentLabel, true, requestGeneration);
    });
    elements.cards.addEventListener('click', function (event) {
        const favourite = event.target.closest('.home-nearby-card__favourite');
        if (favourite) {
            if (event.target === favourite) {
                event.preventDefault();
                const icon = favourite.querySelector('.favourites-icon');
                if (icon) icon.click();
            }
            return;
        }

        const card = event.target.closest('.home-nearby-card[data-profile-url]');
        if (card) window.location.assign(card.dataset.profileUrl);
    });
    elements.cards.addEventListener('keydown', function (event) {
        if (event.target.matches('.home-nearby-card[data-profile-url]')
            && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            window.location.assign(event.target.dataset.profileUrl);
        }
    });

    if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(function (entries) {
            if (!entries.some(function (entry) { return entry.isIntersecting; })) return;
            observer.disconnect();
            initialize();
        }, { rootMargin: '500px 0px' });
        observer.observe(root);
    } else {
        initialize();
    }
})();